import React, { useState } from "react"
import MultiSelect from "src/components/tailwind-components/MultiSelect"
import Button from "src/components/tailwind-components/Button"
import { useGetPartnerPreRegistrationList } from "../hooks/useGetPartnerPreRegistrationList"
import { useUpdatePartnerPreRegistration } from "../hooks/useUpdatePartnerPreRegistration"
import { PartnerPreRegistrationTable } from "./PartnerPreRegistrationTable"
import { AccountManagementModal } from "./AccountManagementModal"
import { useNotification } from "@smoothincorg/smooth-ui"

type PartnerPreRegistrationListProps = {
  defaultStatuses: string[]
}

export const PartnerPreRegistrationList: React.FC<PartnerPreRegistrationListProps> = ({
  defaultStatuses,
}) => {
  const [page, setPage] = useState(1)
  const [statuses, setStatuses] = useState<string[]>(defaultStatuses)
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)
  const { showSnackbar } = useNotification()

  // APIフックからデータを取得
  const { partnerPreRegistrations, pagination, isLoading, error, refresh } =
    useGetPartnerPreRegistrationList({
      statuses: statuses,
      page: page,
    })
  const { updatePartnerPreRegistration } = useUpdatePartnerPreRegistration()

  // 取り下げ
  const handleRevokeClick = async (partnerPreRegistrationId: number) => {
    try {
      await updatePartnerPreRegistration(partnerPreRegistrationId, {}, "revoked", undefined)
      showSnackbar("取り下げしました", "success")
      refresh()
    } catch (error) {
      showSnackbar("取り下げに失敗しました", "warning")
    }
  }

  // 取り下げを解除
  const handleReviveClick = async (partnerPreRegistrationId: number) => {
    try {
      await updatePartnerPreRegistration(partnerPreRegistrationId, {}, "in_review", undefined)
      showSnackbar("取り下げを解除しました", "success")
      refresh()
    } catch (error) {
      showSnackbar("取り下げを解除に失敗しました", "warning")
    }
  }

  // エラー処理
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">データの読み込みに失敗しました</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white">
      <div className="flex items-center justify-between gap-8 mb-8">
        <div className="flex items-center gap-8">
          <div className="flex flex-col">
            <div className="text-[1.6rem] font-bold mb-2">ステータス</div>
            <div className="flex justify-start items-center gap-8" style={{ maxWidth: "600px" }}>
              <MultiSelect
                options={[
                  { value: "in_review", label: "審査待ち" },
                  { value: "in_compliance_review", label: "コンプラ確認" },
                  { value: "approved", label: "通過" },
                  { value: "rejected", label: "否認" },
                  { value: "revoked", label: "取り下げ" },
                  { value: "checking", label: "確認中" },
                ]}
                value={statuses}
                onChange={setStatuses}
              />
            </div>
          </div>
        </div>
        <Button
          type="button"
          mode="info"
          label="アカウント管理"
          onClick={() => setIsAccountModalOpen(true)}
          disabled={false}
        />
      </div>
      <PartnerPreRegistrationTable
        partnerPreRegistrations={partnerPreRegistrations}
        pagination={pagination}
        onRevokeClick={handleRevokeClick}
        onReviveClick={handleReviveClick}
        onPageChange={setPage}
      />
      {isAccountModalOpen && (
        <AccountManagementModal onClose={() => setIsAccountModalOpen(false)} />
      )}
    </div>
  )
}
